import { useCallback } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '~/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '~/components/ui/dialog';
import { Spinner } from '~/components/ui/loader';
import { PRO_BUILDER_PROJECTS_KEY, type ProBuilderBoundApp, type ProBuilderProject } from '~/hooks/queries/use-pro-builder';
import { openAppUrl } from '~/lib/open-app-url';
import { proBuilderKeys } from '~/lib/query-keys';
import { projectDisplayName } from './pro-builder-app-rules';
import { PRO_BUILDER_PANEL_TESTID, proBuilderFocusTarget } from './pro-builder-window-rules';
import { useProBuilderPublishStore, type PublishRowState } from './pro-builder-publish-store';
import { streamPublish, type PublishEvent, type PublishedApp } from './publish-stream';

export type { PublishRowState } from './pro-builder-publish-store';

export const PUBLISH_DESCRIPTION = "Publishing runs the app's build first and replaces the published version completely.";
export const STARTING_MESSAGE = 'Starting…';

/** The same four keys `apps-edit.tsx` invalidates on its own saves, plus this list and the window's bound-app read (its project row carries the app's status). */
export function invalidateAfterPublish(qc: QueryClient, slug: string): void {
  void qc.invalidateQueries({ queryKey: PRO_BUILDER_PROJECTS_KEY });
  void qc.invalidateQueries({ queryKey: proBuilderKeys.app() });
  void qc.invalidateQueries({ queryKey: ['admin-apps'] });
  void qc.invalidateQueries({ queryKey: ['admin-app', slug] });
  void qc.invalidateQueries({ queryKey: ['app-published-card'] });
  void qc.invalidateQueries({ queryKey: ['resource-counts'] });
}

const STOPPED_MESSAGE = 'The publish stopped before it finished. Try again.';

/** How a finished publish is told to the person (`publishNotifier` builds the real one). */
export interface PublishNotify {
  published: (done: { app: PublishedApp; created: boolean; warnings: string[] }) => void;
  failed: (message: string) => void;
}

/** What `runPublish` needs from its surroundings (injected so the logic runs without a renderer). */
export interface PublishRunDeps {
  stream: (name: string, onEvent: (e: PublishEvent) => void) => Promise<void>;
  setRow: (name: string, next: PublishRowState | null) => void;
  setRunning: (name: string, running: boolean) => void;
  onPublished: (slug: string) => void;
  notify: PublishNotify;
}

/**
 * One publish, start to finish. Every failure — the stream's `error` event, a
 * stream that ends without a verdict, a thrown request — lands in the row AND
 * raises an error toast, so it is never silent even when the dialog is closed
 * or the window is showing something else. `running` is marked for the whole
 * run (the header's app switcher, New and Save read it).
 */
export async function runPublish(name: string, deps: PublishRunDeps): Promise<void> {
  const { setRow } = deps;
  let finished = false;
  const fail = (message: string, detail?: string) => {
    setRow(name, { phase: 'error', message, detail });
    deps.notify.failed(message);
  };
  const onEvent = (e: PublishEvent) => {
    if (e.type === 'progress') setRow(name, { phase: 'publishing', message: e.message });
    else if (e.type === 'done') {
      finished = true;
      // Warnings stay under the row until its next publish (empty = nothing to say).
      setRow(name, e.warnings.length ? { phase: 'done', warnings: e.warnings } : null);
      deps.notify.published({ app: e.app, created: e.created, warnings: e.warnings });
      deps.onPublished(e.app.slug);
    } else {
      finished = true;
      fail(e.message, e.detail);
    }
  };
  deps.setRunning(name, true);
  setRow(name, { phase: 'publishing', message: STARTING_MESSAGE });
  try {
    await deps.stream(name, onEvent);
    // A stream that ended without `done` or `error` must not leave a spinner forever.
    if (!finished) fail(STOPPED_MESSAGE);
  } catch (err) {
    fail(err instanceof Error ? err.message : 'Could not publish. Try again.');
  } finally {
    deps.setRunning(name, false);
  }
}

export const SAVED_TO_APPS = 'Saved to Apps';
export const APP_UPDATED = 'App updated';

/** The toast calls `publishNotifier` makes (sonner's `toast` in the app; a recorder in tests). */
type ToastOptions = { description?: string; duration?: number; action?: { label: string; onClick: () => void } };
export interface PublishToasts {
  success: (message: string, options?: ToastOptions) => unknown;
  info: (message: string, options?: ToastOptions) => unknown;
  error: (message: string, options?: ToastOptions) => unknown;
}

/**
 * The guided Builder's save toasts (`coding-session-modal.tsx` `handleSaveApp`):
 * "Saved to Apps" the first time, "App updated" after, each with "Open app",
 * and the first warning as a plain info toast. A failure toasts the server's
 * sentence. The warnings and failure toasts carry "Details", which opens the
 * single-app publish dialog for `folder` — the place the full error detail and
 * every warning are shown.
 */
export function publishNotifier(folder: string, toasts: PublishToasts, openDetails: (folder: string) => void): PublishNotify {
  const details = { label: 'Details', onClick: () => openDetails(folder) };
  return {
    published: ({ app, created, warnings }) => {
      toasts.success(created ? SAVED_TO_APPS : APP_UPDATED, {
        description: created ? `“${app.title}” is in your Apps.` : `“${app.title}” now holds your latest changes.`,
        action: { label: 'Open app', onClick: () => openAppUrl(app.url) },
        duration: 8000,
      });
      if (warnings.length > 0) toasts.info(warnings[0], { duration: 8000, action: details });
    },
    failed: (message) => toasts.error(message, { action: details }),
  };
}

/** The toasts' "Details" opens the dialog only for the folder that is bound right now; for any other it does nothing (the dialog renders only for the bound app). */
export function openDetailsIfBound(folder: string, boundFolder: string | null | undefined, open: (folder: string) => void): void {
  if (boundFolder === folder) open(folder);
}

/**
 * Runs publishes. Two callers: the header's Save disc (a publish with no
 * dialog first) and the details dialog's row. Per-folder state lives in the
 * publish store (`pro-builder-publish-store.ts`), not here, so a publish keeps
 * reporting — toast, row, list refresh — after whichever component started it
 * has unmounted, and the dialog shows the same row the Save disc started.
 */
export function usePublishRunner() {
  const qc = useQueryClient();
  const rows = useProBuilderPublishStore((s) => s.rows);
  const publish = useCallback(
    (name: string) => {
      const store = useProBuilderPublishStore.getState();
      const openDetails = (folder: string) =>
        openDetailsIfBound(folder, qc.getQueryData<ProBuilderBoundApp>(proBuilderKeys.app())?.folder, store.openDetails);
      return runPublish(name, {
        stream: streamPublish,
        setRow: store.setRow,
        setRunning: store.setRunning,
        onPublished: (slug) => invalidateAfterPublish(qc, slug),
        notify: publishNotifier(name, toast, openDetails),
      });
    },
    [qc],
  );
  return { rows, publish };
}

/** What the row says about its app, by status: only a `live` app is "Published". */
export function appStatusLine(app: ProBuilderProject['app']) {
  if (!app) return 'Not published';
  if (app.status === 'archived') return 'Archived';
  if (app.status === 'draft') return 'Not published';
  return (
    <>
      Published ·{' '}
      <a
        href={app.url}
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium text-foreground underline underline-offset-2"
      >
        Open app
      </a>
    </>
  );
}

export function PublishProjectRow({
  project,
  state,
  onPublish,
}: {
  project: ProBuilderProject;
  state: PublishRowState | undefined;
  onPublish: (name: string) => void;
}) {
  // The publish runner outlives this row (the header remounts mid-publish), so the store's running list is the other source of "busy".
  const runningInStore = useProBuilderPublishStore((s) => s.running.includes(project.name));
  const busy = state?.phase === 'publishing' || runningInStore;
  // One decision: the row is disabled for its reason iff it shows that reason's sentence.
  const blockedReason = project.blockedReason;
  const live = project.app?.status === 'live';
  return (
    <li data-testid="pro-builder-project-row" data-project={project.name} className="flex flex-col gap-1.5 py-3 first:pt-0 last:pb-0">
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-foreground">{projectDisplayName(project)}</div>
          <div className="text-xs text-muted-foreground">
            {appStatusLine(project.app)}
          </div>
        </div>
        <Button
          type="button"
          size="sm"
          variant={live ? 'outline' : 'default'}
          disabled={busy || blockedReason !== null}
          onClick={() => onPublish(project.name)}
          data-testid="pro-builder-project-publish"
        >
          {live ? 'Re-publish' : 'Publish'}
        </Button>
      </div>
      {blockedReason !== null && (
        <p data-testid="pro-builder-project-blocked" className="text-xs text-muted-foreground">
          {blockedReason}
        </p>
      )}
      {state?.phase === 'done' && (
        <p data-testid="pro-builder-project-warnings" className="text-xs text-muted-foreground">
          {state.warnings.join(' ')}
        </p>
      )}
      {busy && state?.phase !== 'error' && state?.phase !== 'done' && (
        <div data-testid="pro-builder-project-progress" className="flex items-center gap-2 text-xs text-muted-foreground">
          <Spinner size="sm" className="size-3" />
          <span>{state?.phase === 'publishing' ? state.message : STARTING_MESSAGE}</span>
        </div>
      )}
      {state?.phase === 'error' && (
        <div className="flex flex-col gap-1">
          <p role="alert" data-testid="pro-builder-project-error" className="text-xs text-destructive">
            {state.message}
          </p>
          {state.detail && (
            <details className="text-xs text-muted-foreground">
              <summary className="cursor-pointer select-none">Show details</summary>
              <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap rounded-md bg-muted p-2 font-mono text-[11px] text-foreground">
                {state.detail}
              </pre>
            </details>
          )}
        </div>
      )}
    </li>
  );
}

/** Focus what the window itself would focus on open, if the panel is mounted. */
export function focusProBuilderTerminal(): void {
  const panel = document.querySelector(`[data-testid="${PRO_BUILDER_PANEL_TESTID}"]`);
  if (panel) proBuilderFocusTarget(panel)?.focus();
}

/**
 * The details view of the ONE app the window is bound to (FRONT-3077; it used
 * to list every folder): its progress, the full error behind a failed Save,
 * its warnings, and a Publish / Re-publish button to try again. Opened from
 * the Save toasts' "Details" action. Escape and the
 * close button close only this dialog: it is a Radix layer above the Pro
 * Builder window, which never treats Escape as a close key anyway. Initial
 * focus goes to the dialog panel itself, never to a control that carries a tooltip.
 */
export function ProBuilderPublishDialog({
  open,
  onOpenChange,
  project,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project: ProBuilderProject;
}) {
  const { rows, publish } = usePublishRunner();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="pro-builder-publish-dialog"
        className="sm:max-w-lg"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          (e.currentTarget as HTMLElement | null)?.focus();
        }}
        // Closing must not hand focus back to a tooltip-bearing header control
        // (its tooltip would reopen on focus); it goes to Claude Code instead.
        onCloseAutoFocus={(e) => {
          e.preventDefault();
          focusProBuilderTerminal();
        }}
      >
        <DialogHeader>
          <DialogTitle>{projectDisplayName(project)}</DialogTitle>
          <DialogDescription>{PUBLISH_DESCRIPTION}</DialogDescription>
        </DialogHeader>
        <ul className="flex flex-col">
          <PublishProjectRow project={project} state={rows[project.name]} onPublish={(name) => void publish(name)} />
        </ul>
      </DialogContent>
    </Dialog>
  );
}
